import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ConfigureTab from "@/components/assistentes/tabs/ConfigureTab";
import PromptTab from "@/components/assistentes/tabs/PromptTab";
import KnowledgeBaseTab from "@/components/assistentes/tabs/KnowledgeBaseTab";
import IntegrationsTab from "@/components/assistentes/tabs/IntegrationsTab/IntegrationsTab";
import WebhooksTab from "@/components/assistentes/tabs/WebhooksTab";
import FollowUpTab from "@/components/assistentes/tabs/FollowUpTab";
import AssistantSimulationChat from "@/components/assistentes/AssistantSimulationChat";
import { Button } from "@/components/ui/button";
import { useAssistants } from "@/hooks/useAssistants";
import type { Database } from "@/lib/supabase";
import { DEFAULT_ASSISTANT_RESPONSE_LANGUAGE } from "@/lib/assistantLanguage";
import { DEFAULT_MESSAGE_SPLIT_STYLE } from "@/lib/messageSplitStyle";
import { DEFAULT_AI_MODEL, isLegacyModel } from "@/lib/aiModels";
import { useTranslation } from "react-i18next";

type AccountAssistant = Database["public"]["Tables"]["account_assistants"]["Row"];

type AssistantFormData = {
  name: string;
  model: string;
  response_language: string;
  message_split_style: string;
  response_time_value: number;
  response_time_unit: string;
  max_messages: number;
  prompt_mode: string;
  default_prompt: string;
  voice_tone: string;
  main_objective: string;
  language_rules: string;
  good_examples: string;
  restrictions: string;
  additional_info: string;
  is_active: boolean;
  disable_assistant_on_manual_message: boolean;
};

const mapAssistantToForm = (assistant: AccountAssistant): AssistantFormData => ({
  name: assistant.name ?? "",
  model: assistant.model || DEFAULT_AI_MODEL,
  response_language: assistant.response_language || DEFAULT_ASSISTANT_RESPONSE_LANGUAGE,
  message_split_style: assistant.message_split_style || DEFAULT_MESSAGE_SPLIT_STYLE,
  response_time_value: assistant.response_time_value ?? 10,
  response_time_unit: "seconds",
  max_messages: assistant.max_messages ?? 1,
  prompt_mode: assistant.prompt_mode || "guided-mode",
  default_prompt: assistant.default_prompt || "",
  voice_tone: assistant.voice_tone || "",
  main_objective: assistant.main_objective || "",
  language_rules: assistant.language_rules || "",
  good_examples: assistant.good_examples || "",
  restrictions: assistant.restrictions || "",
  additional_info: assistant.additional_info || "",
  is_active: assistant.is_active ?? true,
  disable_assistant_on_manual_message:
    assistant.disable_assistant_on_manual_message ?? false,
});

const AssistenteEditar: React.FC = () => {
  const { t } = useTranslation("assistants");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { assistants, loading: loadingAssistants, updateAssistant } = useAssistants();

  const [activeTab, setActiveTab] = useState("configure");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [formData, setFormData] = useState<AssistantFormData | null>(null);

  const assistant = assistants.find((a) => a.id === id);

  // Hidrata só no carregamento inicial do assistente e após save (updated_at muda).
  // Evita resetar edições locais a cada novo reference do array `assistants`.
  useEffect(() => {
    if (!assistant) {
      setFormData(null);
      return;
    }
    setFormData(mapAssistantToForm(assistant));
    // Intencional: não depender do objeto `assistant` inteiro (nova referência a cada fetch).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync por id/updated_at
  }, [assistant?.id, assistant?.updated_at]);

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSave = async () => {
    if (!formData?.name.trim()) {
      setMessage({ type: "error", text: t("editor.errNameRequired") });
      return;
    }

    if (!id || !formData) return;

    setSaving(true);
    setMessage(null);

    try {
      // The lock on a legacy assistant's model cannot be visual only: keep the field out of
      // the payload entirely, so no stale form state can switch a model that must not move.
      const { model, ...editableFields } = formData;
      const success = await updateAssistant(id, {
        ...editableFields,
        ...(isLegacyModel(model) ? {} : { model }),
        response_time_unit: "seconds",
      });

      if (success) {
        setMessage({ type: "success", text: t("editor.updated") });
        setTimeout(() => {
          setMessage(null);
        }, 3000);
      } else {
        setMessage({ type: "error", text: t("editor.errUpdateFailed") });
      }
    } catch (error) {
      console.error("Erro ao atualizar assistente:", error);
      setMessage({ type: "error", text: t("editor.errUpdate") });
    } finally {
      setSaving(false);
    }
  };

  if ((loadingAssistants && !assistant) || (assistant && !formData)) {
    return (
      <div className="w-full h-[calc(100dvh-120px)] flex items-center justify-center bg-[#F8FAFC] dark:bg-transparent">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-[#64748B] dark:text-neutral-400">{t("editor.loading")}</p>
        </div>
      </div>
    );
  }

  if (!assistant || !formData) {
    return (
      <div className="w-full h-[calc(100dvh-120px)] flex items-center justify-center bg-[#F8FAFC] dark:bg-transparent">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-neutral-600">
            <svg className="w-8 h-8 text-[#64748B] dark:text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-[#0F172A] mb-2 dark:text-neutral-50">{t("editor.notFound")}</h3>
          <button
            onClick={() => navigate("/assistentes")}
            className="mt-4 px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl text-white transition-colors duration-200"
          >
            {t("common.back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-[calc(100dvh-120px)] flex flex-col">
      <div className="mb-4 flex items-center gap-4">
        <button
          onClick={() => navigate("/assistentes")}
          className="flex items-center gap-2 rounded-xl px-2 py-1.5 text-[#334155] hover:bg-[#F1F5F9] hover:text-[#2563EB] transition-colors duration-200 dark:text-[#D4D4D8] dark:hover:bg-[#27272A] dark:hover:text-[#60A5FA]"
        >
          <ArrowLeft size={20} />
          <span>{t("common.back")}</span>
        </button>
        <h2 className="text-2xl xl:text-3xl font-bold tracking-tight text-[#0F172A] dark:text-[#FAFAFA]">
          {t('editor.title')} {assistant.name}
        </h2>
      </div>

      {message && (
        <div className={`p-3 rounded-xl mb-4 border ${
          message.type === "success"
            ? "bg-[#ECFDF5] border-green-200 text-[#16A34A] dark:bg-green-500/10 dark:border-green-500/30 dark:text-[#22C55E]"
            : "bg-[#FEF2F2] border-red-200 text-[#DC2626] dark:bg-red-500/10 dark:border-red-500/30 dark:text-[#F87171]"
        }`}>
          {message.text}
        </div>
      )}

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-4 xl:gap-6">
        <div className="lg:col-span-8 flex flex-col min-h-0">
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white p-4 md:p-5 shadow-sm dark:border-[#3F3F464D] dark:bg-[#20202040] dark:shadow-none">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col gap-4 min-h-0">
              <TabsList className="mb-0 shrink-0">
                <TabsTrigger value="configure">{t("editor.tabConfigure")}</TabsTrigger>
                <TabsTrigger value="prompt">{t("editor.tabPrompt")}</TabsTrigger>
                <TabsTrigger value="knowledge-base">{t("editor.tabKnowledge")}</TabsTrigger>
                <TabsTrigger value="integrations">{t("editor.tabIntegrations")}</TabsTrigger>
                <TabsTrigger value="webhooks">{t("editor.tabWebhooks")}</TabsTrigger>
                <TabsTrigger value="follow-up">{t("editor.tabFollowUp")}</TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto min-h-0">
                <TabsContent value="configure" className="mt-0">
                  <ConfigureTab
                    key={`configure-${assistant.id}-${assistant.updated_at ?? ""}`}
                    formData={formData}
                    onFormChange={handleFormChange}
                  />
                </TabsContent>
                <TabsContent value="prompt" className="mt-0">
                  <PromptTab formData={formData} onFormChange={handleFormChange} />
                </TabsContent>
                <TabsContent value="knowledge-base" className="mt-0">
                  <KnowledgeBaseTab />
                </TabsContent>
                <TabsContent value="integrations" className="mt-0">
                  {id && <IntegrationsTab assistantId={id} />}
                </TabsContent>
                <TabsContent value="webhooks" className="mt-0">
                  <WebhooksTab />
                </TabsContent>
                <TabsContent value="follow-up" className="mt-0">
                  <FollowUpTab />
                </TabsContent>
              </div>
            </Tabs>

            {(activeTab === "configure" || activeTab === "prompt") && (
              <div className="mt-4 pt-4 border-t border-[#E5E7EB] dark:border-[#3F3F464D]">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-[120px] rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]"
                >
                  {saving ? t("editor.saving") : t("editor.save")}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-4 h-full min-h-[500px] lg:min-h-0">
          <AssistantSimulationChat assistant={{
            ...assistant,
            ...formData,
          }} />
        </div>
      </div>
    </div>
  );
};

export default AssistenteEditar;
